/**
 * Tema Renke — data-theme + localStorage `renke-tema-escolhido`.
 * Default: light. Valores: light | dark | system.
 * Tambem sincroniza a classe `.dark` do shell legado (Tailwind).
 *
 * Regra: só a ESCOLHA da pessoa (menu) grava em localStorage
 * (`escolherTemaRenke`). Aplicar ao abrir o app não grava — antes gravava,
 * e cada troca de padrão exigia uma chave nova para ter efeito.
 */

export type TemaRenke = "light" | "dark" | "system";

/**
 * As chaves anteriores (`renke-theme`, `renke-tema-v2`) gravavam o padrão em
 * todo carregamento, então não refletem escolha de ninguém. Esta só guarda
 * clique no menu.
 */
export const CHAVE_TEMA_RENKE = "renke-tema-escolhido";

export function preferenciaDoSistemaEhEscura(): boolean {
  return globalThis.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
}

export function temaRenkeSalvo(): TemaRenke {
  const salvo = globalThis.localStorage?.getItem(CHAVE_TEMA_RENKE);
  if (salvo === "light" || salvo === "dark" || salvo === "system") return salvo;
  return "light";
}

export function resolverTemaEfetivo(tema: TemaRenke = temaRenkeSalvo()): "light" | "dark" {
  if (tema === "system") {
    return preferenciaDoSistemaEhEscura() ? "dark" : "light";
  }
  return tema;
}

function sincronizarClasseLegado(efetivo: "light" | "dark"): void {
  const raiz = document.documentElement;
  if (efetivo === "dark") {
    raiz.classList.add("dark");
  } else {
    raiz.classList.remove("dark");
  }
}

function comTrocaSemTransicao(aplicar: () => void): void {
  const raiz = document.documentElement;
  raiz.classList.add("rk-theme-switching");
  aplicar();
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      raiz.classList.remove("rk-theme-switching");
    });
  });
}

/** Aplica `data-theme` efetivo (light|dark) no <html>, sem gravar nada. */
export function aplicarTemaRenke(tema: TemaRenke): void {
  const efetivo = resolverTemaEfetivo(tema);
  comTrocaSemTransicao(() => {
    document.documentElement.setAttribute("data-theme", efetivo);
    document.documentElement.style.colorScheme = efetivo;
    sincronizarClasseLegado(efetivo);
  });
}

/** Escolha explícita da pessoa (menu): grava e aplica. */
export function escolherTemaRenke(tema: TemaRenke): void {
  globalThis.localStorage?.setItem(CHAVE_TEMA_RENKE, tema);
  aplicarTemaRenke(tema);
}

/** IIFE para `<head>` — evita flash antes do CSS. Default light. */
export const SCRIPT_ANTI_FLASH_RENKE = `(function(){try{var k='renke-tema-escolhido';var t=localStorage.getItem(k);if(t!=='light'&&t!=='dark'&&t!=='system')t='light';var d=t==='dark'||(t==='system'&&window.matchMedia('(prefers-color-scheme: dark)').matches);var e=d?'dark':'light';document.documentElement.setAttribute('data-theme',e);document.documentElement.style.colorScheme=e;if(e==='dark')document.documentElement.classList.add('dark');else document.documentElement.classList.remove('dark');}catch(err){document.documentElement.setAttribute('data-theme','light');}})();`;
